import { useState } from "react";
import { useInstrumentStore } from "../../state/instrumentStore";
import { useArrangementStore } from "../../state/arrangementStore";
import { snapshotEditorState } from "../../state/snapshot";
import { PITCH_CLASSES } from "../../constants/scale";
import { CaptureSegmentModal } from "./CaptureSegmentModal";
import { ExportButton } from "./ExportButton";
import "./AdvancedPanel.css";

export function AdvancedPanel() {
  const open = useInstrumentStore((s) => s.advancedOpen);
  if (!open) return null;
  return <AdvancedPanelBody />;
}

function AdvancedPanelBody() {
  const rootPitch = useInstrumentStore((s) => s.rootPitch);
  const blueFlat3 = useInstrumentStore((s) => s.blueFlat3);
  const blueFlat5 = useInstrumentStore((s) => s.blueFlat5);
  const setRoot = useInstrumentStore((s) => s.setRoot);
  const setBlueFlat3 = useInstrumentStore((s) => s.setBlueFlat3);
  const setBlueFlat5 = useInstrumentStore((s) => s.setBlueFlat5);
  const closeAdvanced = useInstrumentStore((s) => s.closeAdvanced);

  const arrangementCount = useArrangementStore((s) => s.order.length);
  const selectedSegmentId = useArrangementStore((s) => s.selectedSegmentId);
  const selectedSegment = useArrangementStore((s) =>
    s.selectedSegmentId ? s.segments[s.selectedSegmentId] : null,
  );
  const replaceSegment = useArrangementStore((s) => s.replaceSegment);

  const [captureOpen, setCaptureOpen] = useState(false);

  const handleReplace = () => {
    if (!selectedSegmentId) return;
    replaceSegment(selectedSegmentId, snapshotEditorState());
    closeAdvanced();
  };

  return (
    <div className="advanced-backdrop" onClick={closeAdvanced}>
      <div
        className="advanced-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Advanced settings"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="advanced-header">
          <span className="advanced-title">PARENT MODE</span>
          <button
            type="button"
            className="advanced-close"
            onClick={closeAdvanced}
            aria-label="Close parent mode"
          >
            ✕
          </button>
        </div>

        <section className="advanced-section">
          <h3 className="advanced-heading">KEY</h3>
          <p className="advanced-hint">
            Transposes every piano roll. Recorded melodies stay put.
          </p>
          <div className="advanced-keys">
            {PITCH_CLASSES.map((pc) => (
              <button
                key={pc}
                type="button"
                className={`advanced-key ${rootPitch === pc ? "is-active" : ""}`}
                onClick={() => setRoot(pc)}
                aria-pressed={rootPitch === pc}
              >
                {pc}
              </button>
            ))}
          </div>
        </section>

        <section className="advanced-section">
          <h3 className="advanced-heading">BLUE NOTES</h3>
          <p className="advanced-hint">
            Add extra rows to the pitch grid. Great for bluesy solos.
          </p>
          <div className="advanced-toggles">
            <button
              type="button"
              className={`advanced-toggle ${blueFlat3 ? "is-active" : ""}`}
              onClick={() => setBlueFlat3(!blueFlat3)}
              aria-pressed={blueFlat3}
            >
              ♭3
              <span className="advanced-toggle-sub">flat third</span>
            </button>
            <button
              type="button"
              className={`advanced-toggle ${blueFlat5 ? "is-active" : ""}`}
              onClick={() => setBlueFlat5(!blueFlat5)}
              aria-pressed={blueFlat5}
            >
              ♭5
              <span className="advanced-toggle-sub">flat fifth</span>
            </button>
          </div>
        </section>

        <section className="advanced-section">
          <h3 className="advanced-heading">SAVE</h3>
          <p className="advanced-hint">
            Freeze the current beats, melodies, and FX as a segment. Drop more
            segments into the strip at the bottom to build an arrangement, then
            download the whole thing as a WAV file.
          </p>
          <div className="advanced-save-actions">
            {selectedSegment && (
              <button
                type="button"
                className="advanced-save-replace"
                onClick={handleReplace}
                aria-label={`Update segment ${selectedSegment.name}`}
              >
                UPDATE "{selectedSegment.name.toUpperCase()}"
              </button>
            )}
            <button
              type="button"
              className="advanced-save-capture"
              onClick={() => setCaptureOpen(true)}
              aria-label="Save current pattern as a new segment"
            >
              + {selectedSegment ? "SAVE AS NEW" : "SAVE AS SEGMENT"}
            </button>
            <ExportButton arrangementCount={arrangementCount} />
          </div>
        </section>
      </div>
      {captureOpen && (
        <CaptureSegmentModal onClose={() => setCaptureOpen(false)} />
      )}
    </div>
  );
}
